import { useRef } from 'react';
import { NodeProps, Position, useReactFlow } from 'reactflow';
import { Box, Divider, Typography } from '@mui/material';

import { WorkflowStepType } from '@bandido/shared/constants';
import { PagedResponse, TeamResponseDto } from '@bandido/shared/dtos';
import { useGetAllTeams } from '@frontend/features/teams/queries';

import { WorkflowStepNode } from '../../../types';
import CustomHandle from '../CustomHandle';
import { useWorkflow } from '../../WorkflowContext/hook';
import { SelectedStepModal } from '../SelectedStepModal';
import { ChipsContainer, HeaderContainer, RemainingChipsCounter, StepContainer, TeamChip, TeamDot } from './styles';
import { useVisibleChips } from './hooks';
import { NODE_WIDTH } from './constants';

interface Team {
  id: string;
  name: string;
  color: string;
}

const createTeamsMap = (teamsList?: PagedResponse<TeamResponseDto>): Map<string, Team> =>
  new Map(
    teamsList?.items.map((team) => [
      String(team.id),
      {
        id: String(team.id),
        name: team.name || '',
        color: team.color || '#000000',
      },
    ]) ?? [],
  );

const WorkflowDiagramStep = ({ data, type, xPos, yPos }: NodeProps<WorkflowStepNode>) => {
  const { selectedStep, setSelectedStepId } = useWorkflow();
  const reactFlowInstance = useReactFlow();
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: teamsList } = useGetAllTeams();

  const teamsMap = createTeamsMap(teamsList);
  const teamIds = data.teams.map((id) => String(id));
  const visibleChipsCount = useVisibleChips(containerRef, teamIds, teamsList);
  const visibleTeams = teamIds.slice(0, visibleChipsCount);
  const remainingCount = teamIds.length - visibleChipsCount;
  const isSelected = selectedStep?.id === data.id;

  const handleStepClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedStepId(data.id);

    reactFlowInstance.setCenter(xPos + NODE_WIDTH, yPos, {
      zoom: 1,
      duration: 800,
    });
  };

  const renderTeamChip = (teamId: string) => {
    const team = teamsMap.get(teamId) || { id: teamId, name: '', color: '#000000' };
    return (
      <TeamChip
        key={teamId}
        size="small"
        label={
          <Box sx={{ display: 'flex', gap: 1, ml: 0.5, alignItems: 'center' }}>
            <div>
              <TeamDot color={team?.color} />
            </div>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                height: 22,
              }}
            >
              {team?.name}
            </Typography>
          </Box>
        }
        variant="filled"
      />
    );
  };

  return (
    <>
      <StepContainer
        className={!isSelected ? 'enable-drag-handle' : ''}
        elevation={isSelected ? 4 : 0}
        selected={isSelected}
        isCancelStep={data.isCancelStep}
        onClick={handleStepClick}
      >
        <HeaderContainer>
          <Typography
            sx={{
              color: data.isCancelStep ? 'error.main' : isSelected ? 'primary.main' : 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            variant="subtitle2"
          >
            {data.name}
          </Typography>
        </HeaderContainer>
        <Divider />
        <ChipsContainer ref={containerRef}>
          {visibleTeams.map(renderTeamChip)}
          {remainingCount > 0 && <RemainingChipsCounter size="small" label={`+${remainingCount}`} variant="filled" />}
        </ChipsContainer>
        <>
          {(type === WorkflowStepType.CUSTOM || type === WorkflowStepType.INITIAL) && (
            <CustomHandle style={{ right: -8 }} type="source" position={Position.Right} />
          )}
          {(type === WorkflowStepType.CUSTOM || type === WorkflowStepType.FINAL) && (
            <CustomHandle style={{ left: -8 }} type="target" position={Position.Left} />
          )}
        </>
      </StepContainer>
      {isSelected && <SelectedStepModal />}
    </>
  );
};

export default WorkflowDiagramStep;
